import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, get, post } from '@/shared/api/api-client';
import type {
  GetPatientsLabDetailResponse,
  OrderRequestsListRequest,
} from '@/types/scheduling-api';
import type {
  LabObservationTrend,
  LabRequestReports,
  PatientExternalLabReports,
  PatientLabRequestReports,
} from '@/types/lab-results-api';
import type { LabTrendKey } from '@/patients/lib/lab-results';

/**
 * Chart › Labs discrete results (PROD-2643). Three reads:
 *
 * 1. `POST /api/Patients/GetPatientLabDetails` with `patientId` — the lab desk list, filtered to
 *    this patient. It is the one existing read that returns PatientLabRequest ids (with name,
 *    vendor, date and any linked PDF). The org comes from the `OrganizationId` header the API
 *    client already sends. `GetLabOrderByPatientId` (the orders table) returns ServiceOrderRequest
 *    ids, which the results endpoints do not take.
 * 2. `GET /api/Patients/{patientId}/LabRequests/Reports?labRequestId=…` for all of them in one read
 *    (falls back to `…/LabRequests/{labRequestId}/Reports` per lab request where that is not deployed).
 * 3. `GET /api/Patients/{patientId}/LabObservations?loinc=` (or `scripsComponentCode=` or
 *    `localCode=&localSystem=`, in that precedence) per analyte, for its sparkline.
 *
 * PROD-3275 adds `GET /api/Patients/{patientId}/LabReports?includeHistory=true`: the patient's
 * outside (External) reports, which have no lab request. The chart Labs tab reads every Reports
 * call with `includeHistory=true` so an amended analyte can show its earlier versions.
 *
 * Before the results tables exist the reads answer `storeAvailable: false`, and before
 * Scrips.Patient #997 deploys they 404. Either way the tab stays as it is, so a 4xx is final
 * (no retry). Anything else (a 5xx, a network drop) is retried once, and if it still fails the
 * section says so rather than silently leaving the results out.
 */

/** 404 = the results endpoints are not deployed yet: the same as "store not available". */
export function isResultsNotDeployed(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function retryTransientOnce(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}

/** A patient rarely has more lab requests than this; the list is newest first. */
const LAB_REQUEST_PAGE_SIZE = 100;

/** The lab desk list read for one patient — shared by the hook and by a FRESH re-read (a chart
 *  quote's Refresh, `queryClient.fetchQuery`), so both hit the same key and endpoint. */
export function patientLabRequestsQuery(patientId: string | null | undefined) {
  return {
    queryKey: ['patient-lab-requests', patientId] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) => {
      const body: OrderRequestsListRequest = {
        patientId: patientId ?? null,
        pageNumber: 1,
        pageSize: LAB_REQUEST_PAGE_SIZE,
      };
      return post<GetPatientsLabDetailResponse>('/api/Patients/GetPatientLabDetails', body, {
        signal,
      });
    },
    enabled: !!patientId,
    staleTime: 60_000,
    retry: false,
  };
}

export function usePatientLabRequests(patientId: string | null | undefined) {
  return useQuery(patientLabRequestsQuery(patientId));
}

/** The most lab requests one batched Reports read takes (Scrips.Patient #1226). */
export const LAB_REPORTS_BATCH_SIZE = 100;

/** One lab request's Reports, or why it could not be read. Keyed by lower-case lab request id. */
export type LabRequestReportsById = Record<string, { data?: LabRequestReports; error?: unknown }>;

function notFound(): ApiError {
  return new ApiError(404, 'No such lab request for this patient in your organization.');
}

function reportsUrl(patientId: string, labRequestId: string, includeHistory: boolean): string {
  return `/api/Patients/${encodeURIComponent(patientId)}/LabRequests/${encodeURIComponent(
    labRequestId,
  )}/Reports?includeHistory=${includeHistory}`;
}

/**
 * Every lab request's Reports in ONE request per 100 ids (`GET …/LabRequests/Reports?labRequestId=…`),
 * not one request per lab request: that burst stalled the Patient service when an encounter opened.
 * Where the batched route is not deployed yet (404), it falls back to the per-request reads.
 * An id the server lists in `notFoundLabRequestIds` gets the same 404 the per-request read gave.
 */
export async function readLabRequestReports(
  patientId: string,
  labRequestIds: string[],
  includeHistory: boolean,
  signal?: AbortSignal,
): Promise<LabRequestReportsById> {
  const byId: LabRequestReportsById = {};
  try {
    for (let i = 0; i < labRequestIds.length; i += LAB_REPORTS_BATCH_SIZE) {
      const qs = new URLSearchParams(
        labRequestIds.slice(i, i + LAB_REPORTS_BATCH_SIZE).map((id) => ['labRequestId', id]),
      );
      qs.set('includeHistory', String(includeHistory));
      const batch = await get<PatientLabRequestReports>(
        `/api/Patients/${encodeURIComponent(patientId)}/LabRequests/Reports?${qs}`,
        { signal },
      );
      for (const reads of batch.labRequests ?? []) {
        if (reads.patientLabRequestId) byId[reads.patientLabRequestId.toLowerCase()] = { data: reads };
      }
      for (const id of batch.notFoundLabRequestIds ?? []) {
        byId[id.toLowerCase()] = { error: notFound() };
      }
    }
    // An id the answer leaves out would show as loading for ever: treat it as not found.
    for (const id of labRequestIds) byId[id.toLowerCase()] ??= { error: notFound() };
    return byId;
  } catch (error) {
    if (!isResultsNotDeployed(error)) throw error;
  }
  await Promise.all(
    labRequestIds.map(async (id) => {
      try {
        byId[id.toLowerCase()] = { data: await get<LabRequestReports>(reportsUrl(patientId, id, includeHistory), { signal }) };
      } catch (error) {
        byId[id.toLowerCase()] = { error };
      }
    }),
  );
  return byId;
}

/** The Reports read of a patient's lab requests — shared by the hook and by a fresh re-read. */
export function labRequestReportsQuery(
  patientId: string | null | undefined,
  labRequestIds: string[],
  includeHistory = false,
) {
  // Lower-case, unique and sorted: the same set of lab requests is one cache entry in any order.
  const ids = [...new Set(labRequestIds.map((id) => id.toLowerCase()))].sort();
  return {
    // The note's chart reads keep their own (current-only) key; the Labs tab asks for history.
    queryKey: (includeHistory
      ? (['lab-request-reports', patientId, ids, 'history'] as const)
      : (['lab-request-reports', patientId, ids] as const)),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      readLabRequestReports(patientId ?? '', ids, includeHistory, signal),
    enabled: !!patientId && ids.length > 0,
    staleTime: 60_000,
    retry: retryTransientOnce,
  };
}

/**
 * While a new set of ids loads (a lab request was added), keep showing the reads already in hand, so only
 * the new lab request's card shows as loading. Only for the same patient: never another patient's labs.
 */
export function samePatientsReports(patientId: string | null | undefined) {
  return (previous: LabRequestReportsById | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
    previousQuery?.queryKey[1] === patientId ? previous : undefined;
}

/** One entry per lab request, in the order given, with the fields the callers read from a query. */
export interface LabRequestReportsEntry {
  data?: LabRequestReports;
  isError: boolean;
  error: unknown;
  dataUpdatedAt: number;
}

export function toLabRequestReportsEntries(
  labRequestIds: string[],
  read: { data?: LabRequestReportsById; isError?: boolean; error?: unknown; dataUpdatedAt?: number },
): LabRequestReportsEntry[] {
  return labRequestIds.map((id) => {
    if (read.isError) return { isError: true, error: read.error, dataUpdatedAt: read.dataUpdatedAt ?? 0 };
    const one = read.data?.[id.toLowerCase()];
    return {
      data: one?.data,
      isError: one?.error !== undefined,
      error: one?.error ?? null,
      dataUpdatedAt: read.dataUpdatedAt ?? 0,
    };
  });
}

export function useLabRequestReports(
  patientId: string | null | undefined,
  labRequestIds: string[],
  includeHistory = false,
): LabRequestReportsEntry[] {
  const query = useQuery({
    ...labRequestReportsQuery(patientId, labRequestIds, includeHistory),
    placeholderData: samePatientsReports(patientId),
  });
  const { data, isError, error, dataUpdatedAt } = query;
  // The ids' content, not the array instance: callers build a new array on every render.
  const idsKey = labRequestIds.join(',');
  return useMemo(
    () => toLabRequestReportsEntries(idsKey ? idsKey.split(',') : [], { data, isError, error, dataUpdatedAt }),
    [idsKey, data, isError, error, dataUpdatedAt],
  );
}

/**
 * The patient's outside (External) lab reports, with every version (PROD-3275). A 404 (not
 * deployed) or `storeAvailable: false` means none, the same as the Reports read above.
 */
export function usePatientExternalLabReports(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-external-lab-reports', patientId],
    queryFn: ({ signal }) =>
      get<PatientExternalLabReports>(
        `/api/Patients/${encodeURIComponent(patientId ?? '')}/LabReports?includeHistory=true`,
        { signal },
      ),
    enabled: !!patientId,
    staleTime: 60_000,
    retry: retryTransientOnce,
  });
}

export function trendQueryString(key: LabTrendKey): string {
  const qs = new URLSearchParams();
  if (key.loinc) qs.set('loinc', key.loinc);
  else if (key.scripsComponentCode) {
    qs.set('scripsComponentCode', key.scripsComponentCode);
    if (key.scripsComponentSystem) qs.set('scripsComponentSystem', key.scripsComponentSystem);
  } else if (key.localCode) {
    qs.set('localCode', key.localCode);
    if (key.localSystem) qs.set('localSystem', key.localSystem);
  }
  return qs.toString();
}

export function useLabObservationTrend(
  patientId: string | null | undefined,
  key: LabTrendKey | null,
) {
  const qs = key ? trendQueryString(key) : '';
  return useQuery({
    queryKey: ['lab-observation-trend', patientId, qs],
    queryFn: ({ signal }) =>
      get<LabObservationTrend>(
        `/api/Patients/${encodeURIComponent(patientId ?? '')}/LabObservations?${qs}`,
        { signal },
      ),
    enabled: !!patientId && qs !== '',
    staleTime: 5 * 60_000,
    retry: retryTransientOnce,
  });
}
